import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChildSaveSuccessModal } from '@/components/account/ChildSaveSuccessModal';
import { ConfirmModal } from '@/components/patterns/ConfirmModal';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { clearChildCountCache } from '@/hooks/useChildCount';
import {
  ChildProfileFields,
  type ChildFieldsErrors,
  type ChildFieldsValues,
} from '@/features/children/ChildProfileFields';
import {
  DEFAULT_CHILD_FIELDS_ERRORS,
  DEFAULT_CHILD_FIELDS_VALUES,
} from '@/features/children/child-fields-defaults';
import { createChild, getChild, listChildren, updateChild } from '@/features/children/api';
import { computeAge } from '@/lib/age';
import { ApiError, NetworkError } from '@/lib/api-client';
import type { Child, CreateChildRequest, Gender } from '@/lib/auth-types';
import { splitCSV } from '@/lib/csv';
import { STRINGS } from '@/strings/strings';

const MAX_CHILD_AGE_YEARS = 13;

type Mode = 'new' | 'edit' | 'multi';

type FormErrorVariant = 'load' | 'save' | 'network' | 'list' | null;

function childToFieldsValues(child: Child): ChildFieldsValues {
  return {
    childName: child.name ?? '',
    nickname: child.nickname ?? '',
    birthdate: child.birthdate ?? '',
    gender: (child.gender ?? '') as Gender | '',
    kuscheltier: child.stuffedAnimalName ?? '',
    interests: (child.interests ?? []).join(', '),
    bezugspersonen: (child.referenceRoles ?? []).join(', '),
    orte: (child.places ?? []).join(', '),
    sonstiges: child.contextNotes ?? '',
  };
}

function fieldsValuesToCreatePayload(values: ChildFieldsValues): CreateChildRequest {
  const payload: CreateChildRequest = {
    name: values.childName.trim(),
    age: computeAge(values.birthdate),
    birthdate: values.birthdate,
    gender: values.gender as Gender,
  };
  if (values.nickname.trim()) payload.nickname = values.nickname.trim();
  if (values.kuscheltier.trim()) payload.stuffedAnimalName = values.kuscheltier.trim();
  if (values.sonstiges.trim()) payload.contextNotes = values.sonstiges.trim();
  const interestsList = splitCSV(values.interests);
  if (interestsList.length) payload.interests = interestsList;
  const referenceRolesList = splitCSV(values.bezugspersonen);
  if (referenceRolesList.length) payload.referenceRoles = referenceRolesList;
  const placesList = splitCSV(values.orte);
  if (placesList.length) payload.places = placesList;
  return payload;
}

export function ChildProfileRoute() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryMode = searchParams.get('mode');
  const queryChildId = searchParams.get('childId');

  const mode: Mode = queryMode === 'new' ? 'new' : queryChildId ? 'edit' : 'multi';

  const [values, setValues] = useState<ChildFieldsValues>(DEFAULT_CHILD_FIELDS_VALUES);
  const [errors, setErrors] = useState<ChildFieldsErrors>(DEFAULT_CHILD_FIELDS_ERRORS);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<FormErrorVariant>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  // Multi-Edit dropdown state
  const [children, setChildren] = useState<Child[]>([]);
  const [activeChildId, setActiveChildId] = useState<string | null>(null);
  const [pendingSwitchId, setPendingSwitchId] = useState<string | null>(null);
  const [dirtySwitchOpen, setDirtySwitchOpen] = useState(false);

  const S = STRINGS.account.childProfile;
  const FE = STRINGS.children.fields.errors;

  // ── validators ──
  const validateRequired = (value: string): string | null => (value.trim() ? null : FE.required);

  const validateBirthdate = (value: string): string | null => {
    if (!value) return FE.required;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return FE.invalidDate;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    if (d > now) return FE.futureDate;
    const minD = new Date(now);
    minD.setFullYear(minD.getFullYear() - MAX_CHILD_AGE_YEARS);
    if (d < minD) return FE.tooOld;
    return null;
  };

  // ── load: edit-mode (single child) seeds from GET /api/children/:id ──
  useEffect(() => {
    if (mode !== 'edit' || !queryChildId) return;
    let cancelled = false;
    (async () => {
      try {
        const child = await getChild(queryChildId);
        if (cancelled) return;
        setValues(childToFieldsValues(child));
        setErrors(DEFAULT_CHILD_FIELDS_ERRORS);
        setIsDirty(false);
        setFormError(null);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NetworkError) setFormError('network');
        else setFormError('load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, queryChildId]);

  // ── load: multi-edit lists all children + auto-selects first ──
  useEffect(() => {
    if (mode !== 'multi') return;
    let cancelled = false;
    (async () => {
      try {
        const list = await listChildren();
        if (cancelled) return;
        const first = list[0];
        if (!first) {
          // Verification 1 (Gate-1): bookmarked /account/child_profile
          // with zero children — defensive redirect to mode=new.
          navigate('/account/child_profile?mode=new', { replace: true });
          return;
        }
        setChildren(list);
        setActiveChildId(first.id);
        setValues(childToFieldsValues(first));
        setErrors(DEFAULT_CHILD_FIELDS_ERRORS);
        setIsDirty(false);
        setFormError(null);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NetworkError) setFormError('network');
        else setFormError('list');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, navigate]);

  const handleChildFieldChange = (patch: Partial<ChildFieldsValues>) => {
    setValues((prev) => ({ ...prev, ...patch }));
    if (!isDirty) setIsDirty(true);
    if (patch.childName !== undefined && errors.childName) {
      setErrors((e) => ({ ...e, childName: null }));
    }
    if (patch.birthdate !== undefined && errors.birthdate) {
      setErrors((e) => ({ ...e, birthdate: null }));
    }
    if (patch.gender !== undefined && errors.gender) {
      setErrors((e) => ({ ...e, gender: null }));
    }
  };

  const handleChildFieldBlur = (field: 'childName' | 'birthdate' | 'gender') => {
    if (field === 'childName') {
      setErrors((e) => ({ ...e, childName: validateRequired(values.childName) }));
    } else if (field === 'birthdate') {
      setErrors((e) => ({ ...e, birthdate: validateBirthdate(values.birthdate) }));
    } else {
      setErrors((e) => ({ ...e, gender: values.gender ? null : FE.required }));
    }
  };

  const validateAll = (): boolean => {
    const errChild = validateRequired(values.childName);
    const errBirth = validateBirthdate(values.birthdate);
    const errGen = values.gender ? null : FE.required;
    setErrors({ childName: errChild, birthdate: errBirth, gender: errGen });
    return !errChild && !errBirth && !errGen;
  };

  const submitCreate = async (): Promise<boolean> => {
    setFormError(null);
    setIsSubmitting(true);
    try {
      await createChild(fieldsValuesToCreatePayload(values));
      // Batch 8 Commit 5: invalidate the AccountRoute Kinderprofile-
      // count badge after a successful create. updateChild path
      // doesn't change the count so it skips the call.
      clearChildCountCache();
      setIsSubmitting(false);
      return true;
    } catch (err) {
      setIsSubmitting(false);
      if (err instanceof NetworkError) setFormError('network');
      else if (err instanceof ApiError) setFormError('save');
      else setFormError('save');
      return false;
    }
  };

  const submitUpdate = async (childId: string): Promise<boolean> => {
    setFormError(null);
    setIsSubmitting(true);
    try {
      await updateChild(childId, fieldsValuesToCreatePayload(values));
      setIsSubmitting(false);
      return true;
    } catch (err) {
      setIsSubmitting(false);
      if (err instanceof NetworkError) setFormError('network');
      else setFormError('save');
      return false;
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateAll()) return;

    if (mode === 'new') {
      const ok = await submitCreate();
      if (ok) setSuccessOpen(true);
    } else if (mode === 'edit' && queryChildId) {
      const ok = await submitUpdate(queryChildId);
      if (ok) {
        setIsDirty(false);
        navigate('/account/children');
      }
    } else if (mode === 'multi' && activeChildId) {
      const ok = await submitUpdate(activeChildId);
      if (ok) {
        setIsDirty(false);
        // Refresh list (name may have changed)
        try {
          const list = await listChildren();
          setChildren(list);
        } catch {
          // non-fatal — leave the displayed list as-is
        }
      }
    }
  };

  // Multi-Edit: dropdown switch with dirty-form gate
  const handleSelectChild = async (newId: string) => {
    if (newId === activeChildId) return;
    if (isDirty) {
      setPendingSwitchId(newId);
      setDirtySwitchOpen(true);
      return;
    }
    const next = children.find((c) => c.id === newId);
    if (!next) return;
    setActiveChildId(newId);
    setValues(childToFieldsValues(next));
    setErrors(DEFAULT_CHILD_FIELDS_ERRORS);
    setIsDirty(false);
  };

  const handleDirtySwitchSave = async () => {
    if (!activeChildId || !pendingSwitchId) return;
    if (!validateAll()) return;
    const ok = await submitUpdate(activeChildId);
    if (!ok) return;
    const next = children.find((c) => c.id === pendingSwitchId);
    if (next) {
      setActiveChildId(pendingSwitchId);
      setValues(childToFieldsValues(next));
      setErrors(DEFAULT_CHILD_FIELDS_ERRORS);
      setIsDirty(false);
    }
    setPendingSwitchId(null);
  };

  const handleDirtySwitchDiscard = () => {
    if (!pendingSwitchId) return;
    const next = children.find((c) => c.id === pendingSwitchId);
    if (next) {
      setActiveChildId(pendingSwitchId);
      setValues(childToFieldsValues(next));
      setErrors(DEFAULT_CHILD_FIELDS_ERRORS);
      setIsDirty(false);
    }
    setPendingSwitchId(null);
  };

  // Save-Success-Modal handlers (mode='new' only)
  const handleSuccessAddAnother = () => {
    // Reset form to a fresh new-child slate; URL stays at ?mode=new
    setValues(DEFAULT_CHILD_FIELDS_VALUES);
    setErrors(DEFAULT_CHILD_FIELDS_ERRORS);
    setIsDirty(false);
    setSuccessOpen(false);
    // Ensure the URL reflects mode=new even if it already does
    if (searchParams.get('mode') !== 'new') {
      setSearchParams({ mode: 'new' }, { replace: true });
    }
  };

  const handleSuccessDone = () => {
    setSuccessOpen(false);
    navigate('/account/children');
  };

  const title = mode === 'new' ? S.titleNew : mode === 'edit' ? S.titleEdit : S.titleMulti;
  const submitLabel = mode === 'new' ? S.submitNew : S.submitEdit;

  const formErrorMessage =
    formError === 'network'
      ? S.errors.networkError
      : formError === 'save'
        ? S.errors.saveFailed
        : formError === 'load'
          ? S.errors.loadFailed
          : formError === 'list'
            ? S.errors.listFailed
            : null;

  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer maxWidth="default">
        <div className="py-6">
          <button
            type="button"
            onClick={() => navigate('/account/children')}
            className="mb-4 text-sm text-dream-navy/70 hover:text-dream-navy transition-colors"
          >
            {S.back}
          </button>

          <h1 className="text-[22px] font-semibold text-dream-navy mb-4">{title}</h1>

          {mode === 'multi' && children.length > 0 && (
            <div className="mb-6 space-y-1.5">
              <Label htmlFor="cp-multi-selector" className="text-sm text-dream-navy">
                {S.selectorLabel}
              </Label>
              <select
                id="cp-multi-selector"
                value={activeChildId ?? ''}
                onChange={(e) => void handleSelectChild(e.target.value)}
                className="h-11 w-full rounded-lg bg-dream-white text-dream-navy px-2.5 border border-input focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none"
              >
                {children.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {formErrorMessage && (
            <div
              role="alert"
              className="flex items-start gap-2 mb-4 p-3 rounded-md bg-error/10 border border-error/30 text-error-text text-sm"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{formErrorMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <ChildProfileFields
              values={values}
              onChange={handleChildFieldChange}
              errors={errors}
              onBlurField={handleChildFieldBlur}
              tone="light"
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              className="mt-6 w-full h-12 text-base font-semibold"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? S.submitLoading : submitLabel}
            </Button>
          </form>
        </div>
      </ContentContainer>

      <ChildSaveSuccessModal
        open={successOpen}
        onOpenChange={setSuccessOpen}
        onAddAnother={handleSuccessAddAnother}
        onDone={handleSuccessDone}
      />

      <ConfirmModal
        open={dirtySwitchOpen}
        onOpenChange={(open) => {
          setDirtySwitchOpen(open);
          // Esc / backdrop close: drop the pending switch (do nothing)
          if (!open) setPendingSwitchId(null);
        }}
        headline={S.dirtySwitchModal.headline}
        body={S.dirtySwitchModal.body}
        primaryCta={S.dirtySwitchModal.primaryCta}
        secondaryCta={S.dirtySwitchModal.secondaryCta}
        onConfirm={handleDirtySwitchSave}
        onSecondaryAction={handleDirtySwitchDiscard}
        testId="child-dirty-switch-modal"
      />
    </div>
  );
}
